import { useState } from "react";
import { Navigate } from "react-router";
import { Check, Copy, Download, ExternalLink } from "lucide-react";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
} from "@dg/ui";
import { useAuth } from "../contexts/auth.js";
import {
  PLATFORM_SHELL,
  type Stack,
  type Coupling,
} from "../data/connectors/platform-shell.js";
import {
  PLATFORM_AUTH,
  type AuthStack,
} from "../data/connectors/platform-auth.js";

// ── Types ──────────────────────────────────────────────────────────────────

type ConnectorId = "platform-shell" | "platform-auth";

interface ConnectorMeta {
  id: ConnectorId;
  label: string;
  description: string;
}

const CONNECTORS: ConnectorMeta[] = [
  {
    id: "platform-shell",
    label: "Platform Shell",
    description:
      "Adds the platform header bar, theme sync, and user navigation to your hosted app.",
  },
  {
    id: "platform-auth",
    label: "Platform Auth",
    description:
      "Checks the platform session and redirects unauthenticated users to the platform login. Use in private and restricted apps.",
  },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().split("T")[0] ?? "";
}

function shellDgEntry(stack: Stack, coupling: Coupling): string {
  return JSON.stringify(
    {
      name: "platform-shell",
      shellVersion: "v1",
      stack,
      coupling,
      integratedAt: todayISO(),
    },
    null,
    2,
  );
}

function authDgEntry(stack: AuthStack): string {
  return JSON.stringify(
    { name: "platform-auth", stack, integratedAt: todayISO() },
    null,
    2,
  );
}

function triggerBriefDownload(
  connector: ConnectorId,
  stack: Stack | AuthStack,
  coupling?: Coupling,
): void {
  const params = new URLSearchParams({ connector, stack });
  if (coupling) params.set("coupling", coupling);
  const a = document.createElement("a");
  a.href = `/api/v1/admin/connectors/brief?${params.toString()}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ── Copy button ──────────────────────────────────────────────────────────────

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className="gap-1.5"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
      {copied ? "Copied" : label}
    </Button>
  );
}

// ── Right panel ──────────────────────────────────────────────────────────────

interface RightPanelProps {
  connector: ConnectorMeta | null;
  stack: Stack | null;
  coupling: Coupling;
  onStackChange: (s: Stack) => void;
  onCouplingChange: (c: Coupling) => void;
}

function RightPanel({
  connector,
  stack,
  coupling,
  onStackChange,
  onCouplingChange,
}: RightPanelProps) {
  if (!connector) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
        Select a connector from the list to get started.
      </div>
    );
  }

  const isShell = connector.id === "platform-shell";

  const content =
    stack != null
      ? isShell
        ? PLATFORM_SHELL[stack as Stack][coupling]
        : PLATFORM_AUTH[stack as AuthStack]
      : null;

  const dgEntry =
    stack != null
      ? isShell
        ? shellDgEntry(stack as Stack, coupling)
        : authDgEntry(stack as AuthStack)
      : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">{connector.label}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {connector.description}
        </p>
      </div>

      <Separator />

      {/* Selectors */}
      <div className="flex flex-wrap gap-4">
        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Stack
          </p>
          <Select
            value={stack ?? ""}
            onValueChange={(v) => onStackChange(v as Stack)}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Choose stack…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="html">HTML / CSS / JS</SelectItem>
              <SelectItem value="react-vite">React + Vite</SelectItem>
              <SelectItem value="astro">Astro</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isShell && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Coupling
            </p>
            <Select
              value={coupling}
              onValueChange={(v) => onCouplingChange(v as Coupling)}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tight">Tight</SelectItem>
                <SelectItem value="middle">Middle</SelectItem>
                <SelectItem value="loose">Loose</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Content — visible once a stack is chosen */}
      {content && (
        <>
          <Separator />

          <div className="space-y-2">
            <p className="text-sm font-medium">Integration code</p>
            <pre className="rounded-md bg-muted p-4 text-xs overflow-x-auto whitespace-pre-wrap break-all">
              <code>{content.integrationCode}</code>
            </pre>
            <CopyButton text={content.integrationCode} label="Copy code" />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Claude Code prompt</p>
            <pre className="rounded-md bg-muted p-4 text-xs overflow-x-auto whitespace-pre-wrap">
              <code>{content.claudePrompt}</code>
            </pre>
            <CopyButton text={content.claudePrompt} label="Copy prompt" />
          </div>

          <Separator />

          <div className="flex flex-wrap gap-3 items-center">
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() =>
                triggerBriefDownload(
                  connector.id,
                  stack as Stack | AuthStack,
                  isShell ? coupling : undefined,
                )
              }
            >
              <Download className="h-4 w-4" />
              Download brief .md
            </Button>

            {dgEntry && (
              <CopyButton
                text={dgEntry}
                label="Copy dg-connectors.json entry"
              />
            )}

            <Button variant="ghost" size="sm" className="gap-1.5" asChild>
              <a href="/admin" target="_blank" rel="noopener noreferrer">
                Documentation
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ConnectorsPage() {
  const { auth } = useAuth();

  const [selectedId, setSelectedId] = useState<ConnectorId | null>(null);
  const [stack, setStack] = useState<Stack | null>(null);
  const [coupling, setCoupling] = useState<Coupling>("middle");

  if (auth.status === "loading") {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (auth.status !== "active" || auth.user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  function handleSelectConnector(id: ConnectorId) {
    setSelectedId(id);
    setStack(null);
    setCoupling("middle");
  }

  const selectedConnector = CONNECTORS.find((c) => c.id === selectedId) ?? null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Connectors</h1>

      <div className="flex gap-6 min-h-[400px]">
        {/* Left panel */}
        <nav aria-label="Connectors" className="w-52 shrink-0 space-y-1">
          {CONNECTORS.map((c) => {
            const isActive = selectedId === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => handleSelectConnector(c.id)}
                className={[
                  "w-full text-left rounded-r-md border-l-4 px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "border-sage-500 bg-accent text-accent-foreground font-medium"
                    : "border-transparent text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                ].join(" ")}
              >
                {c.label}
              </button>
            );
          })}
        </nav>

        {/* Divider */}
        <div className="w-px bg-border shrink-0" />

        {/* Right panel */}
        <div className="flex-1 min-w-0">
          <RightPanel
            connector={selectedConnector}
            stack={stack}
            coupling={coupling}
            onStackChange={setStack}
            onCouplingChange={setCoupling}
          />
        </div>
      </div>
    </div>
  );
}
